import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

interface EventsTagProps {
  limit?: number;
}

interface EventRow {
  id: number;
  title: string | null;
  description: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  status: number | null;
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatTime(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = d.getMinutes();
  if (h === 0 && m === 0) return '';
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Balise {{events}} — agenda / événements du marchand.
 * Lit uniquement les événements rattachés au commerce courant. Si le
 * marchand n'en a aucun, le bloc ne s'affiche pas.
 */
export default function EventsTag({ limit }: EventsTagProps) {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;

  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTenant || !merchantId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        let query = supabase
          .from('events')
          .select('id, title, description, start_datetime, end_datetime, status')
          .eq('id_user', String(merchantId))
          .order('start_datetime', { ascending: true });
        if (limit && limit > 0) query = query.limit(limit);
        const { data } = await query;
        if (!cancelled) setEvents((data || []) as EventRow[]);
      } catch {
        /* bloc silencieux */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (events.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-50 text-accent-800 text-xs font-medium mb-3">
            <i className="ri-calendar-event-line"></i>Événements
          </span>
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">Nos prochains rendez-vous</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {events.map((ev) => {
            const date = formatDate(ev.start_datetime);
            const time = formatTime(ev.start_datetime);
            return (
              <article key={ev.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] font-medium text-accent-800 bg-accent-50 px-2.5 py-1 rounded-full w-fit mb-3">
                  <i className="ri-calendar-line"></i>
                  {date || 'Date à venir'}
                  {time && <span className="text-accent-700">· {time}</span>}
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2">{ev.title || 'Événement'}</h3>
                {ev.description && (
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3">{ev.description}</p>
                )}
                {ev.end_datetime && formatDate(ev.end_datetime) && (
                  <p className="text-[11px] text-foreground-400">
                    <i className="ri-time-line mr-1"></i>Jusqu&apos;au {formatDate(ev.end_datetime)}
                  </p>
                )}
                <Link
                  to={`/events/${ev.id}`}
                  className="mt-auto pt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap"
                >
                  Voir l&apos;événement <i className="ri-arrow-right-s-line"></i>
                </Link>
              </article>
            );
          })}
        </div>

        <div className="text-center mt-7">
          <Link to="/events" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap">
            Tous les événements <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}